import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { WithdrawAvailability } from '../views/Earnings/Earnings.types';
import {
  burnULX,
  burnBonusHubULX,
  changeAutoStake,
  changeBonusHubAutoStake,
  claimGamingRewards,
  convertHubToLP,
  getConversionInfo,
  getMyHubsBigWhale,
  getMyHubsGaming,
  getMyHubsPayment,
  getMyHubsStaking,
  getMyHubsSummary,
  getMyHubsTrading,
  getStakingWithdrawalInfo,
  getBonusHubWithdrawalInfo,
  confirmNFTWithdraw,
  confirmBonusHubgNFTWithdraw,
  purchaseBigWhaleVoucher,
  getWiliCoinsSummary,
  updateWillyCoinsSummary,
  getMyHubsTravel,
  getFlipMeCards,
  getMyHubsAkasha,
  getMyHubsPowerPack
} from '../views/Portfolio/Portfolio.actions';
import {
  CurrentVirtualOfficeLicence,
  GetConversionInfoResponse,
  PortfolioBigWhale,
  PortfolioMonthlyActivity,
  PortfolioStakingPaymentGaming,
  PortfolioTrading,
  PortfolioWalletSummary
} from '../views/Portfolio/Portfolio.types';
import { createWithdrawExplorer } from '../views/Earnings/Withdraw/Withdraw.actions';

export interface PortfolioSliceType {
  loading: boolean;
  loadingHubs: boolean;
  loadingULX: boolean;
  loadingAmountToBurn: boolean;
  loadingConversion: boolean;
  loadingDepositUlx: boolean;
  loadingAutoStake: number;
  loadingAutoCompound: boolean;
  loadingWithdrawHubs: boolean;
  loadingVoucher: boolean;
  loadingSummary: boolean;
  withdrawAvailability: WithdrawAvailability | null;
  payment: PortfolioStakingPaymentGaming | null;
  flipMe: PortfolioStakingPaymentGaming | null;
  gaming: PortfolioStakingPaymentGaming | null;
  staking: PortfolioStakingPaymentGaming | null;
  trading: PortfolioTrading | null;
  bigWhale: PortfolioBigWhale | null;
  travelPackages: PortfolioBigWhale | null;
  akashaPackages: PortfolioStakingPaymentGaming | null;
  powerPackPackages: PortfolioStakingPaymentGaming | null;
  summary: any;
  voucher: any;
  currentVirtualOfficeLicence: CurrentVirtualOfficeLicence | null;
  monthlyActivity: PortfolioMonthlyActivity | null;
  lifetimeRank: string;
  qualifiedRank: string;
  walletSummary: PortfolioWalletSummary | null;
  ulxAmountToBurn: number;
  conversionInfo: GetConversionInfoResponse | null;
  conversionModal: boolean;
  depositUlxModal: boolean;
  conversionUserPackageId: number | null;
  loadingRedeemFlipmeCards: boolean;
}

const initialState = {
  loading: false,
  loadingHubs: false,
  loadingULX: false,
  loadingAmountToBurn: false,
  loadingAutoStake: 0,
  loadingAutoCompound: false,
  loadingConversion: false,
  loadingDepositUlx: false,
  loadingWithdrawHubs: false,
  loadingVoucher: false,
  loadingSummary: false,
  loadingRedeemFlipmeCards: false,
  withdrawAvailability: null,
  payment: null,
  gaming: null,
  staking: null,
  trading: null,
  flipMe: null,
  travelPackages: null,
  akashaPackages: null,
  powerPackPackages: null,
  bigWhale: null,
  summary: {},
  voucher: {},
  currentVirtualOfficeLicence: null,
  monthlyActivity: null,
  lifetimeRank: '',
  qualifiedRank: '',
  walletSummary: null,
  conversionInfo: null,
  conversionModal: false,
  depositUlxModal: false,
  conversionUserPackageId: null
} as PortfolioSliceType;

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    removePortfolioState(state) {
      state = initialState;
    },
    toggleConversionModal(state, { payload }) {
      state.conversionModal = payload;
    },
    toggleDepositUlxModal(state, { payload }) {
      state.depositUlxModal = payload;
    },
    setConversionUserPackageId(state, { payload }) {
      state.conversionUserPackageId = payload;
    },
    emptyMyHubsDataWhenPackageBuy(state) {
      state.trading = null;
      state.payment = null;
      state.bigWhale = null;
      state.staking = null;
      state.travelPackages = null;
      state.akashaPackages = null;
      state.powerPackPackages = null;
      state.walletSummary = null;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getMyHubsSummary.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getMyHubsSummary.fulfilled, (state, { payload }) => {
      state.loading = false;

      state.withdrawAvailability = payload?.withdrawAvailability || null;
      state.currentVirtualOfficeLicence =
        payload?.currentVirtualOfficeLicence || null;
      state.lifetimeRank = payload?.lifetimeRank || '';
      state.qualifiedRank = payload?.qualifiedRank || '';
      state.walletSummary = payload?.walletSummary || null;
    });
    builder.addCase(getMyHubsSummary.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getMyHubsTrading.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsTrading.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.trading = payload || null;
    });
    builder.addCase(getMyHubsTrading.rejected, (state) => {
      state.loadingHubs = false;
    });

    // AKASHA PACKAGES
    builder.addCase(getMyHubsPowerPack.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsPowerPack.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.powerPackPackages = payload || null;
    });
    builder.addCase(getMyHubsPowerPack.rejected, (state) => {
      state.loadingHubs = false;
    });

    // AKASHA PACKAGES
    builder.addCase(getMyHubsAkasha.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsAkasha.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.akashaPackages = payload || null;
    });
    builder.addCase(getMyHubsAkasha.rejected, (state) => {
      state.loadingHubs = false;
    });

    // TRAVEL PACKAGES
    builder.addCase(getMyHubsTravel.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsTravel.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.travelPackages = payload || null;
    });
    builder.addCase(getMyHubsTravel.rejected, (state) => {
      state.loadingHubs = false;
    });

    // BIG WHALE
    builder.addCase(getMyHubsBigWhale.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsBigWhale.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.bigWhale = payload || null;
    });
    builder.addCase(getMyHubsBigWhale.rejected, (state) => {
      state.loadingHubs = false;
    });

    //BUG WHALE SUMMARY
    builder.addCase(getWiliCoinsSummary.pending, (state) => {
      state.loadingSummary = true;
    });
    builder.addCase(getWiliCoinsSummary.fulfilled, (state, { payload }) => {
      const now = moment();
      const midnight = moment.utc().startOf('day');

      const secondsSinceMidnight = now.diff(midnight, 'seconds');

      state.loadingSummary = false;

      state.summary.memeCoinsFromBigWhale =
        payload.willyCoinsSummary.memeCoinsFromBigWhale +
        (secondsSinceMidnight /
          payload.willyCoinsSummary.changePeriodInSeconds) *
          payload.willyCoinsSummary.finupCoinsPerChange;

      state.summary.totalMemeCoins =
        payload.willyCoinsSummary.totalMemeCoins +
        (secondsSinceMidnight /
          payload.willyCoinsSummary.changePeriodInSeconds) *
          payload.willyCoinsSummary.totalCoinsPerChange;

      state.summary.memeCoinsFromBring3 =
        payload.willyCoinsSummary.memeCoinsFromBring3 +
        (secondsSinceMidnight /
          payload.willyCoinsSummary.changePeriodInSeconds) *
          payload.willyCoinsSummary.fastStartCoinsPerChange;

      state.summary.changePeriodInSeconds =
        payload.willyCoinsSummary.changePeriodInSeconds;
      state.summary.finupCoinsPerChange =
        payload.willyCoinsSummary.finupCoinsPerChange;
      state.summary.fastStartCoinsPerChange =
        payload.willyCoinsSummary.fastStartCoinsPerChange;
      state.summary.totalCoinsPerChange =
        payload.willyCoinsSummary.totalCoinsPerChange;
    });

    builder.addCase(getWiliCoinsSummary.rejected, (state) => {
      state.loadingSummary = false;
    });

    builder.addCase(updateWillyCoinsSummary.fulfilled, (state, { payload }) => {
      state.summary.totalMemeCoins = payload.totalWillyMemeCoins.totalMemeCoins;

      state.summary.memeCoinsFromBigWhale =
        payload.totalFineUpMemeCoins.memeCoinsFromBigWhale;

      state.summary.memeCoinsFromBring3 =
        payload.totalFastStartUpMemeCoins.memeCoinsFromBring3;
    });

    builder.addCase(getMyHubsPayment.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsPayment.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.payment = payload;
    });
    builder.addCase(getMyHubsPayment.rejected, (state) => {
      state.loadingHubs = false;
    });

    //FLIP ME
    builder.addCase(getFlipMeCards.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getFlipMeCards.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.flipMe = payload;
    });
    builder.addCase(getFlipMeCards.rejected, (state) => {
      state.loadingHubs = false;
    });

    builder.addCase(getMyHubsGaming.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsGaming.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.gaming = payload || null;
    });
    builder.addCase(getMyHubsGaming.rejected, (state) => {
      state.loadingHubs = false;
    });

    builder.addCase(getMyHubsStaking.pending, (state) => {
      state.loadingHubs = true;
    });
    builder.addCase(getMyHubsStaking.fulfilled, (state, { payload }) => {
      state.loadingHubs = false;
      state.staking = payload || null;
    });
    builder.addCase(getMyHubsStaking.rejected, (state) => {
      state.loadingHubs = false;
    });

    builder.addCase(createWithdrawExplorer.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createWithdrawExplorer.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(createWithdrawExplorer.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getStakingWithdrawalInfo.pending, (state) => {
      state.loadingAmountToBurn = true;
    });
    builder.addCase(
      getStakingWithdrawalInfo.fulfilled,
      (state, { payload }) => {
        state.loadingAmountToBurn = false;

        if (payload) {
          state!.ulxAmountToBurn = payload;
        }
      }
    );
    builder.addCase(getStakingWithdrawalInfo.rejected, (state) => {
      state.loadingAmountToBurn = false;
    });

    builder.addCase(getBonusHubWithdrawalInfo.pending, (state) => {
      state.loadingAmountToBurn = true;
    });
    builder.addCase(
      getBonusHubWithdrawalInfo.fulfilled,
      (state, { payload }) => {
        state.loadingAmountToBurn = false;

        if (payload) {
          state!.ulxAmountToBurn = payload;
        }
      }
    );
    builder.addCase(getBonusHubWithdrawalInfo.rejected, (state) => {
      state.loadingAmountToBurn = false;
    });

    builder.addCase(burnULX.pending, (state) => {
      state.loadingAmountToBurn = true;
    });
    builder.addCase(burnULX.fulfilled, (state, { payload }) => {
      state.loadingAmountToBurn = false;

      const index = state.staking?.userPackages.findIndex(
        (p) => p.id === payload
      );

      if (index) {
        state.staking!.userPackages[index].claimDate = moment(
          new Date()
        ).toString();
      }
    });
    builder.addCase(burnULX.rejected, (state) => {
      state.loadingAmountToBurn = false;
    });

    builder.addCase(burnBonusHubULX.pending, (state) => {
      state.loadingAmountToBurn = true;
    });
    builder.addCase(burnBonusHubULX.fulfilled, (state, { payload }) => {
      state.loadingAmountToBurn = false;

      const index = state.staking?.userPackages.findIndex(
        (p) => p.packageId === payload
      );

      if (index !== undefined) {
        state.staking!.userPackages[index].claimDate = moment(
          new Date()
        ).toString();
      }
    });
    builder.addCase(burnBonusHubULX.rejected, (state) => {
      state.loadingAmountToBurn = false;
    });

    builder.addCase(changeAutoStake.pending, (state) => {
      state.loadingAutoCompound = true;
    });
    builder.addCase(changeAutoStake.fulfilled, (state, { payload }) => {
      state.loadingAutoCompound = false;

      const index = state.staking?.userPackages.findIndex(
        (p) => p.id === payload
      );

      if (index) {
        state.staking!.userPackages[index].disableAtNextWeek =
          !state.staking!.userPackages[index].disableAtNextWeek;
        state.staking!.userPackages[index].claimDate = moment(
          new Date()
        ).toString();
      }
    });
    builder.addCase(changeAutoStake.rejected, (state) => {
      state.loadingAutoCompound = false;
    });

    builder.addCase(changeBonusHubAutoStake.pending, (state) => {
      state.loadingAutoCompound = true;
    });
    builder.addCase(changeBonusHubAutoStake.fulfilled, (state, { payload }) => {
      state.loadingAutoCompound = false;

      const index = state.staking?.userPackages.findIndex(
        (p) => p.packageId === payload
      );

      if (index !== undefined) {
        state.staking!.userPackages[index].disableAtNextWeek =
          !state.staking!.userPackages[index].disableAtNextWeek;
        state.staking!.userPackages[index].claimDate = moment(
          new Date()
        ).toString();
      }
    });
    builder.addCase(changeBonusHubAutoStake.rejected, (state) => {
      state.loadingAutoCompound = false;
    });

    builder.addCase(claimGamingRewards.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(claimGamingRewards.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(claimGamingRewards.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getConversionInfo.pending, (state) => {
      state.loadingConversion = true;
    });
    builder.addCase(getConversionInfo.fulfilled, (state, { payload }) => {
      state.loadingConversion = false;
      state.conversionInfo = payload || null;
    });
    builder.addCase(getConversionInfo.rejected, (state) => {
      state.loadingConversion = false;
    });

    builder.addCase(convertHubToLP.pending, (state) => {
      state.loadingConversion = true;
    });
    builder.addCase(convertHubToLP.fulfilled, (state) => {
      state.loadingConversion = false;
      state.conversionInfo = null;
      state.conversionUserPackageId = null;
      state.conversionModal = false;
    });
    builder.addCase(convertHubToLP.rejected, (state) => {
      state.loadingConversion = false;
    });

    //WITHDRAW HUBs
    builder.addCase(confirmNFTWithdraw.pending, (state) => {});
    builder.addCase(confirmNFTWithdraw.fulfilled, (state, { payload }) => {
      if (state.payment && state.payment.userPackages) {
        const index = state.payment.userPackages.findIndex(
          (p) => p.id === payload?.id
        );

        if (index !== -1) {
          state.payment.userPackages[index].dateNftWithdrawal = moment(
            new Date()
          ).toString();
          state.payment.userPackages[index].dateConverted = null;
          state.payment.userPackages[index].isWithdrawable = false;
        }
      } else if (state.gaming && state.gaming.userPackages) {
        const index = state.gaming.userPackages.findIndex(
          (p) => p.id === payload?.id
        );

        if (index !== -1) {
          state.gaming.userPackages[index].dateNftWithdrawal = moment(
            new Date()
          ).toString();
          state.gaming.userPackages[index].dateConverted = null;
          state.gaming.userPackages[index].isWithdrawable = false;
        }
      }
    });
    builder.addCase(confirmNFTWithdraw.rejected, (state, { payload }) => {});

    //WITHDRAW Bonus HUB
    builder.addCase(confirmBonusHubgNFTWithdraw.pending, (state) => {});
    builder.addCase(
      confirmBonusHubgNFTWithdraw.fulfilled,
      (state, { payload }) => {
        // if (state.staking && state.staking.userPackages) {
        //   const index = state.staking.userPackages.findIndex(
        //     (p) => p.type === payload?.type
        //   );
        //   if (index !== -1) {
        //     state.staking.userPackages[index].dateNftWithdrawal = moment(
        //       new Date()
        //     ).toString();
        //     state.staking.userPackages[index].dateConverted = null;
        //     state.staking.userPackages[index].isWithdrawable = false;
        //   }
        // }
      }
    );
    builder.addCase(
      confirmBonusHubgNFTWithdraw.rejected,
      (state, { payload }) => {}
    );

    //VOUCHER PURHCASE
    builder.addCase(purchaseBigWhaleVoucher.pending, (state) => {
      state.loadingVoucher = true;
    });
    builder.addCase(purchaseBigWhaleVoucher.fulfilled, (state, { payload }) => {
      const index = state.bigWhale?.userPackages.findIndex(
        (p) => p.id === payload?.bigWhaleUserPackage.id
      );

      if (index != undefined && payload != undefined) {
        state.bigWhale!.userPackages[index].isMemeBonusActivated = true;
        state.bigWhale!.userPackages[index].dateFinupBonus =
          payload?.bigWhaleUserPackage.dateFinupBonus;
      }

      state.voucher = payload?.userPackage;

      state.loadingVoucher = false;
    });
    builder.addCase(purchaseBigWhaleVoucher.rejected, (state) => {
      state.loadingVoucher = false;
    });

    // builder.addCase(redeemFlipmeCard.fulfilled, (state, { payload }) => {
    //     const index = state.flipMe?.userPackages.findIndex(
    //       (p) => p.id === payload
    //     );

    //     if (index != undefined && payload != undefined) {
    //     state.flipMe!.userPackages[index].isMemeBonusActivated = true;

    //   }
    //  state.flipMe = payload;
    // });
  }
});

export const {
  toggleConversionModal,
  setConversionUserPackageId,
  removePortfolioState,
  emptyMyHubsDataWhenPackageBuy,
  toggleDepositUlxModal
} = portfolioSlice.actions;

export default portfolioSlice.reducer;
